import { useEffect, useMemo, useState } from "react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, ChevronRight, ShieldCheck, Truck, Package, Plus, Wallet, Ticket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import {
  useAddresses,
  useCreateOrderMutation,
  useCreateAddressMutation,
  useMyWallet,
  useUserCoupons,
  useAdminCoupons,
} from "@/hooks/queries";
import { DeliveryType, PaymentMethod, AddressType } from "@/types/enums";
import { getApiErrorMessage } from "@/services/httpClient";
import { Skeleton } from "@/components/ui/skeleton";
import { PATNA_COORDS } from "@/contexts/LocationContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CouponsDialog } from "@/components/checkout/CouponsDialog";

function formatAddress(addr: {
  addressLine1: string;
  addressLine2?: string;
  landmark?: string;
  city: string;
  state: string;
  pincode: string;
}): string {
  return [addr.addressLine1, addr.addressLine2, addr.landmark, `${addr.city} – ${addr.pincode}`, addr.state]
    .filter(Boolean)
    .join(", ");
}

const Checkout = () => {
  const { items, totalPrice, clearCart } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: addresses = [], isLoading: isLoadingAddresses } = useAddresses(isAuthenticated);
  const { data: wallet, isLoading: isLoadingWallet } = useMyWallet(isAuthenticated);
  const createOrder = useCreateOrderMutation();
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);

  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discountAmount: number } | null>(null);
  const [couponsDialogOpen, setCouponsDialogOpen] = useState(false);
  const { data: userCoupons = [] } = useUserCoupons();
  const { data: platformCouponsData } = useAdminCoupons({ isActive: true });
  const platformCoupons = platformCouponsData?.items ?? [];

  const handleCouponApplied = (code: string, discountAmount: number) => {
    setAppliedCoupon({ code, discountAmount });
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    toast({
      title: "Coupon removed",
    });
  };

  const createAddress = useCreateAddressMutation();
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addressForm, setAddressForm] = useState({
    type: String(AddressType.Home),
    name: "",
    phone: "",
    address: "",
    state: "Bihar",
    city: "Patna",
    pincode: "",
    isDefault: false,
  });

  const openAddAddress = () => {
    setAddressForm({
      type: String(AddressType.Home),
      name: "",
      phone: "",
      address: "",
      state: "Bihar",
      city: "Patna",
      pincode: "",
      isDefault: false,
    });
    setAddressDialogOpen(true);
  };

  const handleSaveAddress = async () => {
    if (!addressForm.name.trim() || !addressForm.address.trim() || !addressForm.pincode.trim() || !addressForm.phone.trim()) {
      toast({ title: "Please fill required fields", variant: "destructive" });
      return;
    }

    const parseAddressType = (value: string): AddressType => {
      switch (value) {
        case String(AddressType.Work):
          return AddressType.Work;
        case String(AddressType.Other):
          return AddressType.Other;
        default:
          return AddressType.Home;
      }
    };

    const dto = {
      fullName: addressForm.name.trim(),
      phone: addressForm.phone.trim(),
      addressLine1: addressForm.address.trim(),
      city: addressForm.city.trim(),
      state: addressForm.state.trim(),
      pincode: addressForm.pincode.trim(),
      country: "India",
      latitude: PATNA_COORDS.latitude,
      longitude: PATNA_COORDS.longitude,
      type: parseAddressType(addressForm.type),
      isDefault: addressForm.isDefault,
    };

    try {
      const newAddr = await createAddress.mutateAsync(dto);
      toast({ title: "Address added successfully" });
      setSelectedAddressId(newAddr.id);
      setAddressDialogOpen(false);
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };
  const deliveryFee = 20;
  const platformFee = 5;
  const discountAmount = appliedCoupon ? appliedCoupon.discountAmount : 0;
  const total = Math.max(0, totalPrice + deliveryFee + platformFee - discountAmount);
  const walletBalance = wallet?.availableBalance ?? 0;
  const hasInsufficientBalance = walletBalance < total;

  const defaultAddressId = useMemo(() => {
    const defaultAddr = addresses.find((a) => a.isDefault);
    return defaultAddr?.id ?? addresses[0]?.id ?? null;
  }, [addresses]);

  useEffect(() => {
    if (selectedAddressId == null && defaultAddressId != null) {
      setSelectedAddressId(defaultAddressId);
    }
  }, [defaultAddressId, selectedAddressId]);

  const storeIds = useMemo(() => new Set(items.map((item) => item.product.storeId)), [items]);
  const singleStoreId = storeIds.size === 1 ? Number(items[0]?.product.storeId) : null;

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-3">
          <p className="font-heading text-xl font-bold">Your cart is empty</p>
          <p className="text-muted-foreground text-sm">Add products to proceed to checkout</p>
          <Link to="/products"><Button>Browse Products</Button></Link>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-3">
          <p className="font-heading text-xl font-bold">Sign in to checkout</p>
          <p className="text-muted-foreground text-sm">Login to select a delivery address and place your order</p>
          <Link to="/auth/login"><Button>Login</Button></Link>
        </div>
      </div>
    );
  }

  if (storeIds.size > 1) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-3 max-w-md px-4">
          <p className="font-heading text-xl font-bold">One store at a time</p>
          <p className="text-muted-foreground text-sm">
            Your cart has items from multiple stores. Please checkout with products from a single store.
          </p>
          <Link to="/products"><Button variant="outline">Continue Shopping</Button></Link>
        </div>
      </div>
    );
  }

  const handlePlaceOrder = async () => {
    if (!selectedAddressId || !singleStoreId) {
      toast({ title: "Select a delivery address", variant: "destructive" });
      return;
    }

    if (hasInsufficientBalance) {
      toast({
        title: "Insufficient wallet balance",
        description: "Add funds to your wallet before placing this order.",
        variant: "destructive",
      });
      return;
    }

    const orderPayload = {
      storeId: singleStoreId,
      shippingAddressId: selectedAddressId,
      deliveryType: DeliveryType.PlatformDelivery,
      paymentMethod: PaymentMethod.Wallet,
      couponCode: appliedCoupon ? appliedCoupon.code : undefined,
      items: items.map((item) => ({
        productId: Number(item.product.id),
        quantity: item.quantity,
      })),
    };

    try {
      const order = await createOrder.mutateAsync(orderPayload);
      clearCart();
      toast({ title: "Order placed successfully", description: "Wallet payment completed." });
      navigate(`/order-confirmation/${order.orderNumber}`);
    } catch (error) {
      toast({
        title: "Could not place order",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const isSubmitting = createOrder.isPending;

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-gradient-hero text-primary-foreground py-6">
        <div className="container mx-auto px-4">
          <nav className="flex items-center gap-1.5 text-sm text-primary-foreground/60 mb-2">
            <Link to="/" className="hover:text-primary-foreground transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-primary-foreground">Checkout</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">Checkout</h1>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-card rounded-2xl border border-border/50 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  <h2 className="font-heading font-bold text-lg">Delivery Address</h2>
                </div>
                <Button size="sm" variant="outline" className="gap-1 animate-fade-in" onClick={openAddAddress}>
                  <Plus className="w-3.5 h-3.5" /> Add New
                </Button>
              </div>

              {isLoadingAddresses ? (
                <div className="space-y-3">
                  <Skeleton className="h-20 rounded-xl" />
                  <Skeleton className="h-20 rounded-xl" />
                </div>
              ) : addresses.length === 0 ? (
                <div className="text-center py-6">
                  <p className="text-sm text-muted-foreground mb-3">No saved addresses yet.</p>
                  <Button variant="outline" className="gap-1" onClick={openAddAddress}>
                    <Plus className="w-3.5 h-3.5" /> Add Address
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {addresses.map((addr) => (
                    <label
                      key={addr.id}
                      className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedAddressId === addr.id ? "border-primary bg-primary/5" : "border-border/50 hover:border-border"
                      }`}
                    >
                      <input
                        type="radio"
                        name="address"
                        value={addr.id}
                        checked={selectedAddressId === addr.id}
                        onChange={() => setSelectedAddressId(addr.id)}
                        className="mt-1 accent-primary"
                      />
                      <div>
                        <p className="font-semibold text-sm">{addr.typeText} • {addr.fullName}</p>
                        <p className="text-sm text-muted-foreground mt-0.5">{formatAddress(addr)}</p>
                        <p className="text-xs text-muted-foreground mt-1">{addr.phone}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-card rounded-2xl border border-border/50 p-5">
              <div className="flex items-center gap-2 mb-4">
                <Wallet className="w-5 h-5 text-primary" />
                <h2 className="font-heading font-bold text-lg">Payment Method</h2>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-4 rounded-xl border-2 border-primary bg-primary/5">
                  <Wallet className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-semibold text-sm">Wallet Balance</p>
                    <p className="text-xs text-muted-foreground">
                      {isLoadingWallet ? "Checking balance..." : `Available: ₹${walletBalance.toFixed(0)}`}
                    </p>
                  </div>
                </div>
                {hasInsufficientBalance && !isLoadingWallet && (
                  <p className="text-sm text-destructive">
                    Insufficient wallet balance. Add funds to place this order.
                  </p>
                )}
              </div>
            </motion.div>

            <div className="flex items-center gap-3 p-4 rounded-xl bg-secondary/50 border border-border/50">
              <Truck className="w-5 h-5 text-primary" />
              <p className="text-sm text-muted-foreground">Estimated delivery in 20–40 minutes from {items[0]?.product.storeName}</p>
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="lg:col-span-1">
            <div className="bg-card rounded-2xl border border-border/50 p-5 sticky top-24">
              <h2 className="font-heading font-bold text-lg mb-4">Order Summary</h2>

              <div className="space-y-3 max-h-[300px] overflow-y-auto mb-4">
                {items.map((item) => (
                  <div key={item.product.id} className="flex items-center gap-3">
                    {item.product.image ? (
                      <img src={item.product.image} alt={item.product.name} className="w-12 h-12 rounded-lg object-cover bg-white" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-muted text-muted-foreground flex items-center justify-center shrink-0 border">
                        <Package className="w-5 h-5 opacity-45" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.product.name}</p>
                      <p className="text-xs text-muted-foreground">{item.product.unit} × {item.quantity}</p>
                    </div>
                    <span className="font-semibold text-sm">₹{item.product.price * item.quantity}</span>
                  </div>
                ))}
              </div>

              <Separator className="my-4" />

              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>₹{totalPrice}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Delivery Fee</span><span>₹{deliveryFee}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Platform Fee</span><span>₹{platformFee}</span></div>
                {appliedCoupon && (
                  <div className="flex justify-between text-emerald-600 dark:text-emerald-400 font-semibold">
                    <span>Coupon ({appliedCoupon.code})</span>
                    <span>-₹{appliedCoupon.discountAmount}</span>
                  </div>
                )}
              </div>

              <Separator className="my-4" />

              {/* Coupon Section */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-foreground">Promo Code / Coupon</h3>
                {appliedCoupon ? (
                  <div className="flex items-center justify-between p-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 animate-fade-in">
                    <div className="flex items-center gap-2">
                      <Ticket className="w-4 h-4 shrink-0" />
                      <div className="text-left">
                        <p className="text-xs font-bold font-mono">{appliedCoupon.code}</p>
                        <p className="text-[10px] opacity-80">Saved ₹{appliedCoupon.discountAmount}</p>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" className="h-7 text-xs font-semibold hover:bg-emerald-500/20 hover:text-emerald-800" onClick={handleRemoveCoupon}>
                      Remove
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setCouponsDialogOpen(true)}
                    className="w-full flex items-center justify-between p-3 rounded-xl border border-dashed border-primary/30 hover:border-primary bg-primary/5 hover:bg-primary/10 text-primary transition-all text-xs font-semibold"
                  >
                    <div className="flex items-center gap-2">
                      <Ticket className="w-4 h-4 shrink-0" />
                      <span>Apply Coupon / View Offers</span>
                    </div>
                    {(userCoupons.length > 0 || platformCoupons.length > 0) && (
                      <span className="text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-md">
                        {userCoupons.filter((c) => c.status === 0).length + platformCoupons.length} Offers
                      </span>
                    )}
                  </button>
                )}
              </div>

              <Separator className="my-4" />

              <div className="flex justify-between items-center mb-5">
                <span className="font-heading font-bold text-lg">Total</span>
                <span className="font-heading font-bold text-xl text-primary">₹{total}</span>
              </div>

              <Button
                onClick={handlePlaceOrder}
                disabled={isSubmitting || !selectedAddressId || isLoadingWallet || hasInsufficientBalance}
                className="w-full h-12 bg-gradient-primary hover:opacity-90 text-primary-foreground font-heading font-semibold text-base rounded-xl"
              >
                {isSubmitting ? "Placing Order..." : `Pay with Wallet • ₹${total}`}
              </Button>

              <div className="flex items-center gap-2 justify-center mt-4 text-xs text-muted-foreground">
                <ShieldCheck className="w-4 h-4" />
                <span>Secure wallet payment</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <CouponsDialog
        open={couponsDialogOpen}
        onOpenChange={setCouponsDialogOpen}
        onApply={handleCouponApplied}
        orderAmount={totalPrice}
        storeId={singleStoreId ?? undefined}
      />

      <Dialog open={addressDialogOpen} onOpenChange={setAddressDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Address</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Label</Label>
              <Select value={addressForm.type} onValueChange={(v) => setAddressForm({ ...addressForm, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(AddressType.Home)}>Home</SelectItem>
                  <SelectItem value={String(AddressType.Work)}>Office</SelectItem>
                  <SelectItem value={String(AddressType.Other)}>Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Name *</Label><Input value={addressForm.name} onChange={(e) => setAddressForm({ ...addressForm, name: e.target.value })} /></div>
            <div className="space-y-2"><Label>Phone *</Label><Input value={addressForm.phone} onChange={(e) => setAddressForm({ ...addressForm, phone: e.target.value })} /></div>
            <div className="space-y-2"><Label>Address *</Label><Input value={addressForm.address} onChange={(e) => setAddressForm({ ...addressForm, address: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>City</Label><Input value={addressForm.city} onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} /></div>
              <div className="space-y-2"><Label>Pincode *</Label><Input value={addressForm.pincode} onChange={(e) => setAddressForm({ ...addressForm, pincode: e.target.value })} /></div>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>Set as default</Label>
              <Switch checked={addressForm.isDefault} onCheckedChange={(v) => setAddressForm({ ...addressForm, isDefault: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddressDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveAddress} disabled={createAddress.isPending}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Checkout;
